import classNames from "classnames";
import React, {
  useState,
  ComponentPropsWithRef,
  useRef,
  ReactNode,
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

import styles from "./index.module.css";
import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";

export type TTextFieldProps = Readonly<
  ComponentPropsWithRef<"input"> & {
    error?: string | boolean;
    icon?: ReactNode | string;
    onEnter?: () => void;
    endAdornment?: ReactNode | string;
    label?: ReactNode | string;
    helperText?: string | ReactNode;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    isRequired?: boolean;
    containerClassName?: string;
    initialFocus?: boolean;
    startAdornment?: ReactNode;
    hint?: ReactNode;
    regexPattern?: { pattern: RegExp; error: string };
    startAdornmentClassName?: string;
    inputClassName?: string;
    readOnly?: boolean;
  }
>;

const Textbox = forwardRef((props: TTextFieldProps, ref) => {
  const {
    name,
    hint,
    value = "",
    type = "text",
    icon,
    onChange = () => null,
    onBlur = () => null,
    onEnter = () => null,
    onFocus = () => null,
    disabled = false,
    label,
    error,
    endAdornment,
    className,
    containerClassName,
    isRequired = false,
    initialFocus = false,
    helperText,
    startAdornment,
    regexPattern,
    startAdornmentClassName,
    inputClassName,
    readOnly = false,
    ...restProps
  } = props;

  const [isFocus, setIsFocus] = useState(false);
  // Add internal state for immediate UI updates
  const [internalValue, setInternalValue] = useState(value);
  const textboxRef = useRef<HTMLInputElement>(null);

  // Sync internal value with prop value when it changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useImperativeHandle(ref, () => textboxRef.current!, []);

  const activeFocus = (e: any) => {
    if (disabled || readOnly) return;
    onFocus(e);
    if (textboxRef.current) textboxRef.current?.focus();
    setIsFocus(true);
  };

  const deactiveFocus = () => setIsFocus(false);

  const onKeyDownHandle = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;
    if (e.key === "Enter") {
      onEnter();
    }
  };

  useEffect(() => {
    if (textboxRef.current && initialFocus) textboxRef.current?.focus();
  }, [textboxRef]);

  const onLocalChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Update internal state immediately for UI responsiveness
    setInternalValue(e.target.value);
    // Call the external onChange (which might be debounced)
    onChange(e);
  };

  const isSatisfyingRegex = value
    ? regexPattern?.pattern.test(value.toString())
    : true;

  return (
    <div
      className={classNames("w-full select-none leading-6", containerClassName)}
    >
      <div
        className={classNames(`${styles.textbox}`, {
          [styles.err]: !!error || (regexPattern && !isSatisfyingRegex),
          [styles.active]:
            !!label && (isFocus || internalValue || internalValue === 0),
        })}
      >
        <div className="flex relative">
          <input
            {...restProps}
            ref={textboxRef}
            className={classNames(
              `flex-1 disabled:border-gray-200 dark:text-gray-100 disabled:shadow-none disabled:opacity-30 disabled:bg-gray-100 transition-all  hover:rounded-md  hover:border-background-100  active:border active:border-slate-200  active:bg-background-50 focus:!border-blue-400 focus:border-[2px] ${inputClassName}`,
              { "!pt-4": !!label },
              className,
              { "!text-[unset]": isFocus },
              {
                "!ps-10": !!startAdornment,
              },
              {
                "cursor-default !bg-gray-50 dark:!bg-gray-800 !border-gray-200 !opacity-70 select-none pointer-events-none":
                  readOnly,
              },
            )}
            onChange={onLocalChange}
            value={internalValue}
            onFocus={activeFocus}
            onBlur={(e) => {
              deactiveFocus();
              onBlur(e);
            }}
            name={name}
            type={type}
            onKeyDown={onKeyDownHandle}
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            disabled={disabled}
          />
        </div>
        {!!label && (
          <label
            className={`${disabled ? "opacity-30 text-gray-400" : ""}${readOnly ? " text-gray-500 cursor-default" : ""}`}
            onClick={activeFocus}
            htmlFor={name}
          >
            {typeof icon !== "string" && icon && <>{icon}</>}
            {typeof icon === "string" && icon && <img src={icon} />}
            <div className={"flex"}>
              {label}
              {isRequired && <span className={"ml-0.5 text-[16px]"}>*</span>}
            </div>
          </label>
        )}
        {!!endAdornment && (
          <div className="absolute inset-y-0  flex justify-center items-center right-4">
            {endAdornment}
          </div>
        )}

        {!!startAdornment && (
          <div
            className={classNames(
              `absolute inset-y-0 left-3 flex justify-center items-center  ${startAdornmentClassName}`,
              {
                "border-gray-200 shadow-none opacity-30": disabled,
                "opacity-80": readOnly,
              },
            )}
          >
            {startAdornment}
          </div>
        )}
      </div>

      {(!!error || (regexPattern && !isSatisfyingRegex)) && (
        <div className="ps-2 pt-2 flex justify-start items-center gap-1">
          <SvgIcon
            className={"w-3 h-3"}
            fill="fill-red-600"
            iconName="attentionRed"
          />
          <Typography
            className="text-start"
            color={"text-red-600"}
            variant={"Caption1-Regular"}
          >
            {error ?? regexPattern?.error}
          </Typography>
        </div>
      )}
      {!!helperText && !error && (
        <div className="flex ps-2 pt-2">
          <Typography color={"text-gray-500"} variant={"Caption1-Regular"}>
            {helperText}
          </Typography>
        </div>
      )}
      {!!hint && (
        <div className="flex ps-2 pt-2">
          <Typography color={"text-gray-500"} variant={"Caption1-Regular"}>
            {hint}
          </Typography>
        </div>
      )}
    </div>
  );
});

Textbox.displayName = "TextField";

export default Textbox;
